import {
  ACTIVE_MATCH_MODE,
  FIELD,
  LABEL_TO_ROLE,
  PREFERRED_SHIRTS_BY_ROLE,
  PLAYER,
  playerRoleProfile,
  type Player,
  type TeamSide,
  type TacticalRole,
} from "@golazo/shared";
import { clone, copyInto, vec } from "../core/math.js";

function tacticRoleFromLabel(label: string | undefined, role: Player["role"]): TacticalRole {
  if (role === "gk") return "gk";
  return LABEL_TO_ROLE[label ?? ""] ?? "cm";
}

function playerRadius(role: Player["role"]): number {
  const base = ACTIVE_MATCH_MODE.physics.playerRadius;
  return role === "gk" ? base * PLAYER.GK_RADIUS_SCALE : base;
}

function pickShirtNumber(
  role: TacticalRole,
  used: Set<number>,
  roleCount: number,
): number {
  const pool = PREFERRED_SHIRTS_BY_ROLE[role];
  for (let i = 0; i < pool.length; i++) {
    const n = pool[(roleCount + i) % pool.length]!;
    if (!used.has(n)) return n;
  }
  for (let n = 1; n <= 99; n++) {
    if (!used.has(n)) return n;
  }
  return 99;
}

function createPlayer(
  side: TeamSide,
  spec: (typeof ACTIVE_MATCH_MODE.formation)[number],
  index: number,
  shirtNumber: number,
): Player {
  const x = side === "home" ? FIELD.WIDTH * spec.depth : FIELD.WIDTH * (1 - spec.depth);
  const y = FIELD.HEIGHT * spec.lane;
  const pos = vec(x, y);
  const tacticRole = tacticRoleFromLabel(spec.label, spec.role);
  // El perfil sale del ROL y de nada mas: el dorsal es aspecto.
  const profile = playerRoleProfile({ tacticRole });

  return {
    id: `${side}-${index}`,
    side,
    role: spec.role,
    tacticRole,
    shirtNumber,
    nickname: null,
    human: false,
    pos,
    prevPos: clone(pos),
    spawn: clone(pos),
    vel: vec(),
    radius: playerRadius(spec.role),
    facing: vec(side === "home" ? 1 : -1, 0),
    steerIntent: vec(),
    aimDir: vec(side === "home" ? 1 : -1, 0),
    chargeSteer: false,
    aimSweep: 0,
    charge: 0,
    dashCooldown: 0,
    tackleCooldown: 0,
    trapTime: 0,
    burstTime: 0,
    hesitateTime: 0,
    fakeCooldown: 0,
    skillCooldown: 0,
    diveTime: 0,
    diveDir: vec(),
    diveCooldown: 0,
    jumpTime: 0,
    jumpCooldown: 0,
    wantsCloseControl: false,
    releaseGrace: 0,
    stamina: 1,
    stumbleTime: 0,
    braceTime: 0,
    firstTimeSwing: null,
    speedMultiplier: profile.speedMultiplier,
  };
}

export interface TeamCreateOptions {
  /** Dorsal del jugador controlado (solo en su lado, `humanSide`). */
  humanShirtNumber?: number;
  /** El lado del humano. Sin definir, el local (lo de siempre). */
  humanSide?: TeamSide;
  /**
   * Asiento del humano en su equipo. Por defecto el del modo
   * (`controlledIndex`, el mas atacante); la carrera lo cambia segun tu
   * posicion (delantero, medio o defensa) y tu pie bueno.
   */
  controlledIndex?: number;
}

export function createTeamPlayers(side: TeamSide, options: TeamCreateOptions = {}): Player[] {
  const used = new Set<number>();
  const roleCounts: Partial<Record<TacticalRole, number>> = {};
  const mine = side === (options.humanSide ?? "home");
  const controlled = mine ? (options.controlledIndex ?? ACTIVE_MATCH_MODE.controlledIndex) : -1;
  const humanNumber =
    mine && options.humanShirtNumber !== undefined
      ? clampShirt(options.humanShirtNumber)
      : null;

  if (humanNumber !== null) used.add(humanNumber);

  return ACTIVE_MATCH_MODE.formation.map((spec, index) => {
    const role = tacticRoleFromLabel(spec.label, spec.role);
    const count = roleCounts[role] ?? 0;
    roleCounts[role] = count + 1;

    const number =
      index === controlled && humanNumber !== null
        ? humanNumber
        : pickShirtNumber(role, used, count);
    used.add(number);
    return createPlayer(side, spec, index, number);
  });
}

function clampShirt(n: number): number {
  return Math.max(1, Math.min(99, Math.round(n)));
}

export function createAllPlayers(options: TeamCreateOptions = {}): Player[] {
  return [...createTeamPlayers("home", options), ...createTeamPlayers("away", options)];
}

export function resetPlayers(players: readonly Player[]): void {
  for (const player of players) {
    copyInto(player.pos, player.spawn);
    copyInto(player.prevPos, player.spawn);
    player.vel.x = 0;
    player.vel.y = 0;
    player.facing.x = player.side === "home" ? 1 : -1;
    player.facing.y = 0;
    player.steerIntent.x = 0;
    player.steerIntent.y = 0;
    player.aimDir.x = player.facing.x;
    player.aimDir.y = 0;
    player.chargeSteer = false;
    player.aimSweep = 0;
    // el nickname se conserva entre saques
    player.charge = 0;
    player.dashCooldown = 0;
    player.tackleCooldown = 0;
    player.trapTime = 0;
    player.burstTime = 0;
    player.hesitateTime = 0;
    player.fakeCooldown = 0;
    player.skillCooldown = 0;
    player.diveTime = 0;
    player.diveDir.x = 0;
    player.diveDir.y = 0;
    player.diveCooldown = 0;
    player.jumpTime = 0;
    player.jumpCooldown = 0;
    player.wantsCloseControl = false;
    player.releaseGrace = 0;
    player.stumbleTime = 0;
    player.braceTime = 0;
    // Un gesto armado no puede sobrevivir a un saque de centro.
    player.firstTimeSwing = null;
  }
}

export function controlledPlayerId(side: TeamSide = "home"): string {
  return `${side}-${ACTIVE_MATCH_MODE.controlledIndex}`;
}
